import React from 'react';
import { Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { COUPON_CHIP_THEME, TalkRoom, TalkSession } from '../../types/talk';
import { calculateDaysBetween, formatDate, formatDateWithTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

interface TalkSessionTableProps {
  room: TalkRoom;
}

const TalkSessionTable: React.FC<TalkSessionTableProps> = ({ room }) => {
  const navigate = useNavigate();
  // 新データ形式：session情報がある場合
  if (room.sessions && room.sessions.length > 0) {
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f7f7f7' }}>
              <TableCell sx={{ fontWeight: 'bold', width: '180px' }}>プラン</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>期間 (日)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>開始日</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>終了日</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {room.sessions.map((session: TalkSession) => (
              <TableRow key={session.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={session.price > 0 ? `${session.price.toLocaleString()}円` : '無料'}
                      size="small"
                      color={session.price > 0 ? "error" : "default"}
                      variant="outlined"
                      sx={{
                        ...(session.price > 0 ? {
                          fontWeight: 'bold',
                          borderColor: 'error.main',
                          color: 'error.main'
                        } : {})
                      }}
                    />
                    {/* クーポン表示 */}
                    {session.coupon_id && session.coupon_code && (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {session.coupon_name ? `${session.coupon_name}（${session.coupon_code}）` : session.coupon_code}
                            </Typography>
                            {session.coupon_discount_amount && (
                              <Typography variant="body2">
                                割引額: ¥{session.coupon_discount_amount.toLocaleString()}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              クーポン詳細ページへ
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Chip
                          icon={<LocalOfferOutlinedIcon/>}
                          label="クーポン"
                          size="small"
                          onClick={() => navigate(`/coupons/${session.coupon_id}/usages`)}
                          sx={{
                            backgroundColor: COUPON_CHIP_THEME.BACKGROUND,
                            color: COUPON_CHIP_THEME.COLOR,
                            borderColor: COUPON_CHIP_THEME.BORDER,
                            border: '1px solid',
                            cursor: 'pointer',
                            '& .MuiChip-icon': {
                              color: COUPON_CHIP_THEME.COLOR,
                            },
                            '&:hover': {
                              backgroundColor: COUPON_CHIP_THEME.BACKGROUND_HOVER,
                            }
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{session.period_days ? `${session.period_days}日` : '-'}</TableCell>
                <TableCell>
                  {session.start_at ?
                    (session.end_at && new Date(session.end_at) < new Date() ?
                      `${getHumanReadableTimeAgo(session.start_at)} （ ${formatDateWithTime(session.start_at, 'jp')} ）` :
                      formatDate(session.start_at)) :
                    '-'}
                </TableCell>
                <TableCell>
                  {session.end_at ?
                    (new Date(session.end_at) < new Date() ?
                      `${getHumanReadableTimeAgo(session.end_at)} （ ${formatDateWithTime(session.end_at, 'jp')} ）` :
                      formatDate(session.end_at)) :
                    '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
  // v1のデータ形式または情報なし
  else {
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f7f7f7' }}>
              <TableCell sx={{ fontWeight: 'bold', width: '180px' }}>プラン</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>期間 (日)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>開始日</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>終了日</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <Chip
                  label="無料"
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                {/* 期間計算 */}
                {(room.start_at && room.end_at) ?
                  `${calculateDaysBetween(room.start_at, room.end_at) || '-'}日` :
                  '-'}
              </TableCell>
              <TableCell>
                {/* 開始日 - 終了日が過去の場合は人間が読みやすい形式、それ以外は元の形式 */}
                {room.start_at ?
                  (room.end_at && new Date(room.end_at) < new Date() ?
                    `${getHumanReadableTimeAgo(room.start_at)} （ ${formatDateWithTime(room.start_at, 'jp')} ）` :
                    formatDate(room.start_at)) :
                  '-'}
              </TableCell>
              <TableCell>
                {/* 終了日 - 過去の場合は人間が読みやすい形式、それ以外は元の形式 */}
                {room.end_at ?
                  (new Date(room.end_at) < new Date() ?
                    `${getHumanReadableTimeAgo(room.end_at)} （ ${formatDateWithTime(room.end_at, 'jp')} ）` :
                    formatDate(room.end_at)) :
                  '-'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
};
export default TalkSessionTable;
